import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiEndpoint } from '../../../../config/api';
import {
  FaTicketAlt, FaSearch, FaFilter, FaChevronRight, FaClock,
  FaExclamationTriangle, FaUserCheck, FaHourglassHalf, FaCheckCircle,
  FaInbox, FaChartLine
} from 'react-icons/fa';
import '../../../pages/Support/Support.css';

const STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', waiting_on_user: 'Waiting on User',
  resolved: 'Resolved', closed: 'Closed'
};
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const CATEGORY_LABELS = {
  general: 'General', bug_report: 'Bug Report', feature_request: 'Feature Request',
  access_issue: 'Access Issue', billing: 'Billing', compliance: 'Compliance',
  data_request: 'Data Request', onboarding: 'Onboarding', other: 'Other'
};

const SupportPanel = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ byStatus: {}, byPriority: {}, avgResponseTimeMinutes: null });
  const [staffList, setStaffList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '', priority: '', category: '', search: '', sort: 'priority'
  });
  const [showFilters, setShowFilters] = useState(false);

  const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('limit', '100');

      const [ticketRes, statsRes, staffRes] = await Promise.all([
        fetch(getApiEndpoint(`/api/support/tickets?${params}`), { headers }),
        fetch(getApiEndpoint('/api/support/tickets/stats'), { headers }),
        fetch(getApiEndpoint('/api/support/tickets/admin/staff'), { headers })
      ]);

      if (ticketRes.ok) {
        const data = await ticketRes.json();
        setTickets(data.tickets || []);
        setTotal(data.total || 0);
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (staffRes.ok) setStaffList(await staffRes.json());
    } catch (e) {
      console.error('Failed to fetch support data:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const formatResponseTime = (mins) => {
    if (!mins) return '—';
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.round(mins / 60)}h`;
    return `${Math.round(mins / 1440)}d`;
  };

  const openCount = stats.byStatus?.open || 0;
  const inProgressCount = stats.byStatus?.in_progress || 0;
  const waitingCount = stats.byStatus?.waiting_on_user || 0;
  const urgentCount = stats.byPriority?.urgent || 0;
  const highCount = stats.byPriority?.high || 0;

  if (loading) {
    return (
      <div className="sup-admin-panel">
        <div className="sup-loading"><div className="sup-spinner" /><p>Loading support dashboard...</p></div>
      </div>
    );
  }

  return (
    <div className="sup-admin-panel">
      {/* Summary Cards */}
      <div className="sup-admin-stats">
        <div className="sup-admin-stat-card sup-stat-open" onClick={() => setFilters(f => ({ ...f, status: 'open' }))}>
          <FaInbox className="sup-admin-stat-icon" />
          <div className="sup-admin-stat-body">
            <span className="sup-admin-stat-count">{openCount}</span>
            <span className="sup-admin-stat-label">Open</span>
          </div>
        </div>
        <div className="sup-admin-stat-card sup-stat-progress" onClick={() => setFilters(f => ({ ...f, status: 'in_progress' }))}>
          <FaHourglassHalf className="sup-admin-stat-icon" />
          <div className="sup-admin-stat-body">
            <span className="sup-admin-stat-count">{inProgressCount}</span>
            <span className="sup-admin-stat-label">In Progress</span>
          </div>
        </div>
        <div className="sup-admin-stat-card sup-stat-waiting" onClick={() => setFilters(f => ({ ...f, status: 'waiting_on_user' }))}>
          <FaUserCheck className="sup-admin-stat-icon" />
          <div className="sup-admin-stat-body">
            <span className="sup-admin-stat-count">{waitingCount}</span>
            <span className="sup-admin-stat-label">Waiting</span>
          </div>
        </div>
        <div className="sup-admin-stat-card sup-stat-urgent" onClick={() => setFilters(f => ({ ...f, priority: 'urgent' }))}>
          <FaExclamationTriangle className="sup-admin-stat-icon" />
          <div className="sup-admin-stat-body">
            <span className="sup-admin-stat-count">{urgentCount + highCount}</span>
            <span className="sup-admin-stat-label">High / Urgent</span>
          </div>
        </div>
        <div className="sup-admin-stat-card sup-stat-response">
          <FaChartLine className="sup-admin-stat-icon" />
          <div className="sup-admin-stat-body">
            <span className="sup-admin-stat-count">{formatResponseTime(stats.avgResponseTimeMinutes)}</span>
            <span className="sup-admin-stat-label">Avg Response</span>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="sup-toolbar">
        <div className="sup-search-wrap">
          <FaSearch className="sup-search-icon" />
          <input
            type="text"
            className="sup-search"
            placeholder="Search by ref, subject, or user..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>
        <button className={`sup-btn sup-btn-ghost ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
          <FaFilter /> Filters
        </button>
        <span className="sup-result-count">{total} ticket{total !== 1 ? 's' : ''}</span>
      </div>

      {showFilters && (
        <div className="sup-filters sup-admin-filters">
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}>
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}>
            <option value="">All Categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={filters.sort} onChange={e => setFilters(f => ({ ...f, sort: e.target.value }))}>
            <option value="priority">Priority</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="updated">Recently Updated</option>
          </select>
          <button className="sup-btn sup-btn-ghost" onClick={() => setFilters({ status: '', priority: '', category: '', search: '', sort: 'priority' })}>
            Clear All
          </button>
        </div>
      )}

      {/* Ticket Table */}
      {tickets.length === 0 ? (
        <div className="sup-empty">
          <FaCheckCircle className="sup-empty-icon" style={{ color: 'var(--cs-green)' }} />
          <h3>All clear</h3>
          <p>No tickets match your current filters.</p>
        </div>
      ) : (
        <div className="sup-admin-table-wrap">
          <table className="sup-admin-table">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Subject</th>
                <th>User</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Category</th>
                <th>Assigned</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} className="sup-admin-row" onClick={() => navigate(`/support/tickets/${t.id}`)}>
                  <td className="sup-admin-ref">{t.ticket_ref}</td>
                  <td className="sup-admin-subject">
                    <span className="sup-admin-subject-text">{t.subject}</span>
                    {t.property_name && <span className="sup-admin-property">{t.property_name}</span>}
                  </td>
                  <td className="sup-admin-user">{t.user_first_name} {t.user_last_name}</td>
                  <td>
                    <span className={`sup-badge sup-badge-status sup-status-${t.status}`}>{STATUS_LABELS[t.status]}</span>
                  </td>
                  <td>
                    <span className={`sup-badge sup-badge-priority sup-priority-${t.priority}`}>
                      {t.priority === 'urgent' && <FaExclamationTriangle />} {PRIORITY_LABELS[t.priority]}
                    </span>
                  </td>
                  <td className="sup-admin-cat">{CATEGORY_LABELS[t.category] || t.category}</td>
                  <td className="sup-admin-assigned">
                    {t.assignee_first_name ? `${t.assignee_first_name} ${t.assignee_last_name}` : <span className="sup-unassigned">—</span>}
                  </td>
                  <td className="sup-admin-time"><FaClock /> {timeAgo(t.updated_at || t.created_at)}</td>
                  <td><FaChevronRight className="sup-chevron" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupportPanel;
